import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle, Fingerprint } from 'lucide-react';
import { useManualSubmit, useMinigameBackground } from '../hooks';
import { Icon } from '../../foundation/primitives';
import { JudgementModal, StartGate, FailOverlay, MinigameHud } from '../../foundation/semantic';
import { useMinigameTimer } from '../../foundation/hooks';
import { resolveMediaRefToUrl } from '../../../utils/mediaRef';

// 既定パズル＝従来のハードコードデモ。config 不在（または不正）時はこの内容に
// フォールバックし、ゼロ config の見た目・挙動を従来と不変に保つ（R0 回帰安全）。
const DEFAULT_TOOLS = [
  { id: 'powder', icon: '🧂', label: '粉末' },
  { id: 'uv', icon: '🔦', label: 'UV' },
  { id: 'camera', icon: '📷', label: '撮影' },
];
const DEFAULT_CORRECT_ORDER = ['powder', 'uv', 'camera'];
const DEFAULT_TITLE = '指紋採取';
const DEFAULT_INSTRUCTION = '正しい手順で指紋を採取せよ';
const DEFAULT_SUCCESS_TITLE = '採取成功！';
// R1: 既定文言は常体（敬体「取得しました」を排除）
const DEFAULT_SUCCESS_SUBTITLE = '指紋データを取得した';

const isNonEmptyString = (value) => typeof value === 'string' && value.length > 0;

/** AssetRef（{ assetId?, url? }）を表示可能な URL に解決する。解決不能なら null */
const resolveAssetRefUrl = (ref) => {
  if (!ref || typeof ref !== 'object') return null;
  return resolveMediaRefToUrl(ref.url ?? ref.assetId ?? null);
};

const Template02Fingerprint = ({ hostSubmit, hostConfig, hostMeta, registryId = 'minigame_fingerprint_02', templateId = 2 }) => {
  // 確立パターン: configKey（JSON 文字列）で config 実体の変化を検知し、
  // 変化時のみ state をリセットする（毎レンダーの参照変化では揺らさない）。
  const configKey = useMemo(() => JSON.stringify(hostConfig ?? {}), [hostConfig]);
  const resolvedConfig = useMemo(() => {
    const config = hostConfig ?? {};

    // tools: id/label 必須。型不正の要素は除外し、全滅なら既定ツールへ。
    const configTools = Array.isArray(config.tools)
      ? config.tools.filter((tool) => tool && isNonEmptyString(tool.id) && isNonEmptyString(tool.label))
      : [];
    const usesDefaultTools = configTools.length === 0;
    const tools = (usesDefaultTools ? DEFAULT_TOOLS : configTools).map((tool) => ({
      ...tool,
      imageUrl: resolveAssetRefUrl(tool.image),
    }));

    // correctOrder: tools に存在する id のみ採用。空になったら
    // 「config ツールの並び順」→「既定手順」の順でフォールバック。
    const toolIds = new Set(tools.map((tool) => tool.id));
    const configOrder = Array.isArray(config.correctOrder)
      ? config.correctOrder.filter((id) => toolIds.has(id))
      : [];
    const correctOrder =
      configOrder.length > 0 ? configOrder : usesDefaultTools ? DEFAULT_CORRECT_ORDER : tools.map((tool) => tool.id);

    return {
      tools,
      correctOrder,
      usesDefaultTools,
      title: isNonEmptyString(config.title) ? config.title : DEFAULT_TITLE,
      instructionText: isNonEmptyString(config.instructionText) ? config.instructionText : DEFAULT_INSTRUCTION,
      toolbeltPosition: config.toolbeltPosition === 'bottom' ? 'bottom' : 'right',
      fingerprintImageUrl: resolveAssetRefUrl(config.fingerprintImage),
      fingerprintImageAlt: isNonEmptyString(config.fingerprintImage?.alt) ? config.fingerprintImage.alt : '',
      stageVisuals: Array.isArray(config.stageVisuals)
        ? config.stageVisuals.filter((visual) => visual && isNonEmptyString(visual.afterToolId))
        : [],
      successTitle: isNonEmptyString(config.successMessage?.title) ? config.successMessage.title : DEFAULT_SUCCESS_TITLE,
      successSubtitle: isNonEmptyString(config.successMessage?.subtitle)
        ? config.successMessage.subtitle
        : DEFAULT_SUCCESS_SUBTITLE,
      // R1: 失敗オーバーレイ文言（BaseConfig.failMessage）。null なら FailOverlay の既定 or
      // 失敗理由別の既定 subtitle を使う
      failTitle: isNonEmptyString(config.failMessage?.title) ? config.failMessage.title : null,
      failSubtitle: isNonEmptyString(config.failMessage?.subtitle) ? config.failMessage.subtitle : null,
      failAllowRetry: config.failMessage?.allowRetry !== false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey]);

  const [currentTool, setCurrentTool] = useState(null);
  const [steps, setSteps] = useState([]);
  const [gameState, setGameState] = useState('playing');
  const [feedback, setFeedback] = useState(null);
  // R1: 開始ゲート（体験文法統一）。step.ui.showTutorial === false の時のみ最初から閉じる
  const [gateOpen, setGateOpen] = useState(() => hostMeta?.ui?.showTutorial !== false);
  // R1: ミス数・失敗理由・やり直し回数（retryCount は全 submit payload に同梱）
  const [mistakes, setMistakes] = useState(0);
  const [failReason, setFailReason] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);
  const backgroundStyle = useMinigameBackground(hostConfig);
  const lastConfigKeyRef = useRef(configKey);

  // R1 ミス上限: config.maxMistakes 優先 → hostMeta.maxMistakes（step 側）。step schema は
  // 0 を「未設定」とするため、どちらも >0 のみ採用。未設定なら無制限（現行どおり）。
  const mistakeLimit = Number.isFinite(hostConfig?.maxMistakes) && hostConfig.maxMistakes > 0
    ? Math.floor(hostConfig.maxMistakes)
    : Number.isFinite(hostMeta?.maxMistakes) && hostMeta.maxMistakes > 0
      ? Math.floor(hostMeta.maxMistakes)
      : null;
  // R1 実動タイマー: config.timeLimitMs 優先 → hostMeta.timeLimitMs。0 以下は useMinigameTimer
  // 側で無効化＝表示も強制も無し（ゼロ config 回帰安全）。
  // config.showTimer === false は「表示のみ」抑止で、満了 fail の強制は維持する。
  const limitMs = Number.isFinite(hostConfig?.timeLimitMs)
    ? hostConfig.timeLimitMs
    : Number.isFinite(hostMeta?.timeLimitMs)
      ? hostMeta.timeLimitMs
      : null;
  const showTimerChip = hostConfig?.showTimer !== false;
  // R1: ゲート表示中・決着後は止める。満了で fail 状態へ
  const { remainingMs, reset: resetTimer } = useMinigameTimer({
    limitMs,
    running: !gateOpen && gameState === 'playing',
    onExpire: () => {
      if (gameState !== 'playing') return;
      setFailReason('timeout');
      setGameState('fail');
    }
  });

  // config 実体が差し替わった時のみ進行状態をリセット（初回マウントでは何もしない）
  useEffect(() => {
    if (lastConfigKeyRef.current === configKey) return;
    lastConfigKeyRef.current = configKey;
    setCurrentTool(null);
    setSteps([]);
    setGameState('playing');
    setFeedback(null);
    setMistakes(0);
    setFailReason(null);
    setRetryCount(0);
    resetTimer();
    resetSubmission();
  }, [configKey, resetSubmission, resetTimer]);

  const totalSteps = resolvedConfig.correctOrder.length;

  const handleToolSelect = (toolId) => {
    setCurrentTool(toolId);
  };

  const handleApply = () => {
    if (!currentTool || gameState !== 'playing') return;

    const expectedTool = resolvedConfig.correctOrder[steps.length];
    if (currentTool === expectedTool) {
      const newSteps = [...steps, currentTool];
      setSteps(newSteps);
      const stageVisual = resolvedConfig.stageVisuals.find((visual) => visual.afterToolId === currentTool);
      setFeedback({
        type: 'success',
        message: isNonEmptyString(stageVisual?.message) ? stageVisual.message : '成功！',
      });
      if (newSteps.length === totalSteps) {
        setTimeout(() => setGameState('success'), 500);
      }
    } else {
      setFeedback({ type: 'error', message: '手順が違う！' });
      // R1: ミス上限到達（n 回目のミス）で fail。中央フィードバックを見せてから遷移する
      const nextMistakes = mistakes + 1;
      setMistakes(nextMistakes);
      if (mistakeLimit != null && nextMistakes >= mistakeLimit) {
        setFailReason('mistakes');
        setTimeout(() => setGameState('fail'), 500);
      }
    }
    setCurrentTool(null);
    setTimeout(() => setFeedback(null), 800);
  };

  const reset = () => {
    setCurrentTool(null);
    setSteps([]);
    setGameState('playing');
    setFeedback(null);
    setMistakes(0);
    setFailReason(null);
    resetSubmission();
  };

  // 段階色（進捗比で汎用化。既定3手順時は従来と同一: 0=border→1=hint→2=accent→3=success）
  const progressRatio = totalSteps > 0 ? steps.length / totalSteps : 0;
  const fingerprintColorVar =
    steps.length === 0
      ? 'var(--mg-border)'
      : progressRatio >= 1
        ? 'var(--mg-success)'
        : progressRatio < 0.5
          ? 'var(--mg-hint)'
          : 'var(--mg-accent)';

  const isComplete = totalSteps > 0 && steps.length >= totalSteps;
  const appliedStageVisuals = resolvedConfig.stageVisuals.filter((visual) => steps.includes(visual.afterToolId));
  const isBottomBelt = resolvedConfig.toolbeltPosition === 'bottom';

  return (
    <div className="t02-root relative w-full h-full overflow-hidden" style={backgroundStyle ?? undefined}>
      <div className="t02-bg-overlay absolute inset-0" />

      {/* R1: 共通 HUD（旧タイトルチップ＋onClick の無い飾りメニューを撤去して置換）。
          タイマー/ミスは設定時のみ表示（showTimer=false は表示のみ抑止・満了 fail は維持）。
          進捗は下部の3分割バーに寄せ、HUD とは二重にしない。?＝開始ゲート再表示 */}
      <MinigameHud
        title={resolvedConfig.title}
        icon="Fingerprint"
        timer={showTimerChip && remainingMs != null ? { remainingMs, totalMs: limitMs } : null}
        mistakes={mistakeLimit != null ? { current: mistakes, max: mistakeLimit } : null}
        onHelp={() => setGateOpen(true)}
      />

      <div className={isBottomBelt ? 'absolute inset-0 pt-16 pb-52' : 'absolute inset-0 pt-16 pb-36 pr-16'}>
        <div className="relative w-full h-full p-4">
          <div
            className={`t02-canvas relative w-full h-full border-2 rounded-xl flex items-center justify-center cursor-pointer transition-all ${currentTool ? 't02-canvas--active' : ''}`}
            onClick={handleApply}
          >
            <div className="relative">
              {resolvedConfig.fingerprintImageUrl ? (
                <img
                  src={resolvedConfig.fingerprintImageUrl}
                  alt={resolvedConfig.fingerprintImageAlt}
                  className="w-32 h-32 object-contain transition-all"
                />
              ) : (
                <Fingerprint
                  className="w-32 h-32 transition-all"
                  style={{ color: fingerprintColorVar }}
                />
              )}
              {/* 既定パズル時のみの段階レイヤ（従来デモの powder/uv 演出を温存） */}
              {resolvedConfig.usesDefaultTools && steps.includes('powder') && (
                <div className="t02-fp-layer t02-fp-layer--powder absolute inset-0 rounded-full" />
              )}
              {resolvedConfig.usesDefaultTools && steps.includes('uv') && (
                <div className="t02-fp-layer t02-fp-layer--uv absolute inset-0 animate-pulse rounded-full" />
              )}
              {/* config 駆動の段階演出（stageVisuals）。色は config 由来のため inline で適用 */}
              {appliedStageVisuals.map((visual, index) => (
                <div
                  key={`${visual.afterToolId}-${index}`}
                  className={`t02-fp-layer absolute inset-0 rounded-full ${visual.animation === 'pulse' || visual.animation === 'glow' ? 'animate-pulse' : ''}`}
                  style={{
                    backgroundColor: isNonEmptyString(visual.overlayColor) ? visual.overlayColor : undefined,
                    opacity: typeof visual.overlayOpacity === 'number' ? visual.overlayOpacity : 0.25,
                    boxShadow:
                      visual.animation === 'glow' && isNonEmptyString(visual.overlayColor)
                        ? `0 0 24px 6px ${visual.overlayColor}`
                        : undefined,
                  }}
                />
              ))}
              {isComplete && (
                <CheckCircle
                  className="t02-fp-check absolute -top-2 -right-2 w-8 h-8"
                />
              )}
            </div>
            {currentTool && (
              <div className="t02-tool-prompt absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg text-sm">
                タップで {resolvedConfig.tools.find(t => t.id === currentTool)?.label} を使用
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        className={
          isBottomBelt
            ? 'absolute bottom-40 left-0 right-0 z-30 flex flex-row flex-wrap justify-center gap-2 px-4'
            : 'absolute right-2 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-2'
        }
      >
        {resolvedConfig.tools.map((tool) => {
          const isUsed = steps.includes(tool.id);
          const isActive = currentTool === tool.id;
          return (
            <button
              key={tool.id}
              disabled={isUsed}
              className={`t02-toolrail w-12 h-12 rounded-full flex items-center justify-center text-xl transition-all ${isActive ? 't02-toolrail--active' : isUsed ? 't02-toolrail--used' : 't02-toolrail--idle'}`}
              onClick={() => !isUsed && handleToolSelect(tool.id)}
            >
              {isUsed ? (
                <CheckCircle className="t02-toolrail-check w-5 h-5" />
              ) : tool.imageUrl ? (
                <img src={tool.imageUrl} alt={tool.label} className="w-8 h-8 rounded-full object-cover" />
              ) : (
                tool.icon || tool.label.charAt(0)
              )}
            </button>
          );
        })}
      </div>

      {feedback && (
        <div className={`t02-feedback absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40 px-6 py-3 rounded-xl font-bold ${feedback.type === 'success' ? 't02-feedback--success' : 't02-feedback--error'}`}>
          {feedback.message}
        </div>
      )}

      {/* R1: 説明一行は StartGate へ移設。下部カードは3分割バー＋カウントのみ残す */}
      <div className="t02-bottom absolute bottom-0 left-0 right-0 z-30 pt-4 pb-6 px-4">
        <div className="t02-progress-card rounded-xl p-3">
          <div className="flex items-center gap-2">
            {resolvedConfig.correctOrder.map((_, i) => (
              <div
                key={i}
                className={`t02-progress-bar flex-1 h-2 rounded-full ${i < steps.length ? 't02-progress-bar--filled' : 't02-progress-bar--empty'}`}
              />
            ))}
            <span className="t02-progress-count text-sm font-mono ml-2">{steps.length}/{totalSteps}</span>
          </div>
        </div>
      </div>

      {/* Foundation: 成功モーダル（中央バッジ + タイトル + 説明 + 次へ進む を JudgementModal に集約。
          R1: payload に retryCount を同梱） */}
      <JudgementModal
        open={gameState === 'success'}
        type="success"
        title={resolvedConfig.successTitle}
        description={resolvedConfig.successSubtitle}
        icon={<Icon name="Fingerprint" size={36} />}
        buttonLabel="次へ進む"
        onSubmit={() => submit('success', { retryCount })}
      />

      {/* R1: 失敗（タイマー満了 / ミス上限到達）は semantic FailOverlay に統一。
          文言は config.failMessage（title/subtitle/allowRetry）を優先し、不在時は
          FailOverlay 既定＋失敗理由別の subtitle。未整理のまま進む → submit('fail') */}
      {gameState === 'fail' && (
        <FailOverlay
          title={resolvedConfig.failTitle ?? undefined}
          subtitle={resolvedConfig.failSubtitle ?? (failReason === 'timeout' ? '時間切れ' : 'ミスが多すぎた')}
          allowRetry={resolvedConfig.failAllowRetry}
          onRetry={() => {
            setRetryCount((count) => count + 1);
            reset();
            resetTimer();
          }}
          onProceed={() => submit('fail', { retryCount })}
        />
      )}

      {/* R1: 開始ゲート（体験文法統一）。開始までゲーム操作・タイマーは始まらない。
          HUD の ? で随時再表示（表示中はタイマー一時停止）。説明一行（instructionText）はここに集約 */}
      {gateOpen && (
        <StartGate
          title={resolvedConfig.title}
          icon="Fingerprint"
          instruction={resolvedConfig.instructionText}
          onStart={() => setGateOpen(false)}
        />
      )}
    </div>
  );
};

export default Template02Fingerprint;
